/**
 * RNNoise real-time noise suppression filter.
 *
 * Uses @jitsi/rnnoise-wasm (WASM compiled from Xiph RNNoise) to remove
 * background noise from the microphone stream before it reaches Telnyx.
 *
 * Pipeline: Mic → getUserMedia → AudioContext → RNNoise ScriptProcessor → clean MediaStream
 *
 * The cleaned stream is passed to TelnyxRTC via the `localStream` option,
 * so Telnyx sends noise-free audio to the remote party.
 */
import createRNNWasmModule from '@jitsi/rnnoise-wasm/dist/rnnoise';

const RNNOISE_FRAME_SIZE = 480; // 10ms at 48kHz — fixed by RNNoise

let cachedModule = null;

/**
 * Load and cache the RNNoise WASM module (singleton).
 */
async function getRNNoiseModule() {
  if (cachedModule) return cachedModule;

  cachedModule = await createRNNWasmModule({
    locateFile: (file) => {
      if (file.endsWith('.wasm')) return '/rnnoise.wasm';
      return file;
    },
  });

  return cachedModule;
}

/**
 * Create a noise-filtered MediaStream from a raw microphone stream.
 *
 * @param {MediaStream} rawStream - The raw getUserMedia stream
 * @returns {{ stream: MediaStream, cleanup: Function }}
 *   - stream: noise-filtered MediaStream to pass to Telnyx
 *   - cleanup: call this when the call ends to free resources
 */
export async function createFilteredStream(rawStream) {
  const module = await getRNNoiseModule();

  // Initialize RNNoise
  module._rnnoise_init();
  const state = module._rnnoise_create(0);

  if (!state) {
    console.warn('RNNoise: failed to create state, returning raw stream');
    return { stream: rawStream, cleanup: () => {} };
  }

  // Allocate WASM memory for input/output frames
  const inputPtr = module._malloc(RNNOISE_FRAME_SIZE * 4);
  const outputPtr = module._malloc(RNNOISE_FRAME_SIZE * 4);

  // Audio processing graph
  const audioCtx = new AudioContext({ sampleRate: 48000 });
  const source = audioCtx.createMediaStreamSource(rawStream);

  // ScriptProcessorNode: buffer size 4096 is a good balance of latency vs efficiency
  // (4096 / 48000 = ~85ms processing chunks, but output latency is frame-level)
  const processor = audioCtx.createScriptProcessor(4096, 1, 1);

  // Ring buffer to handle the mismatch between 4096-sample blocks and 480-sample RNNoise frames
  let inputRing = new Float32Array(RNNOISE_FRAME_SIZE);
  let outputRing = new Float32Array(4096 + RNNOISE_FRAME_SIZE); // oversize for safety
  let ringIn = 0;   // write position in inputRing
  let ringOut = 0;  // read position in outputRing
  let ringOutLen = 0; // samples available in outputRing

  processor.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    const output = e.outputBuffer.getChannelData(0);

    // Feed all input samples through RNNoise, one 480-sample frame at a time
    let outWritePos = 0;

    for (let i = 0; i < input.length; i++) {
      inputRing[ringIn++] = input[i];

      if (ringIn === RNNOISE_FRAME_SIZE) {
        // Frame complete — process through RNNoise
        const heapIn = new Float32Array(module.HEAPF32.buffer, inputPtr, RNNOISE_FRAME_SIZE);
        for (let j = 0; j < RNNOISE_FRAME_SIZE; j++) {
          heapIn[j] = inputRing[j] * 32768; // float [-1,1] → int16 scale
        }

        module._rnnoise_process_frame(state, outputPtr, inputPtr);

        const heapOut = new Float32Array(module.HEAPF32.buffer, outputPtr, RNNOISE_FRAME_SIZE);
        for (let j = 0; j < RNNOISE_FRAME_SIZE; j++) {
          outputRing[outWritePos++] = heapOut[j] / 32768; // int16 scale → float [-1,1]
        }

        ringIn = 0;
      }
    }

    // Copy processed samples to output (may be slightly fewer than input due to framing)
    for (let i = 0; i < output.length; i++) {
      output[i] = i < outWritePos ? outputRing[i] : 0;
    }
  };

  const destination = audioCtx.createMediaStreamDestination();
  source.connect(processor);
  processor.connect(destination);

  console.log('RNNoise: noise suppression filter active');

  return {
    stream: destination.stream,
    cleanup: () => {
      try {
        source.disconnect();
        processor.disconnect();
        destination.disconnect();
      } catch (e) { /* ignore */ }
      try {
        module._rnnoise_destroy(state);
        module._free(inputPtr);
        module._free(outputPtr);
      } catch (e) { /* ignore */ }
      try {
        audioCtx.close();
      } catch (e) { /* ignore */ }
      console.log('RNNoise: filter cleaned up');
    },
  };
}

/**
 * Check if RNNoise is supported in this browser.
 */
export function isRNNoiseSupported() {
  return typeof WebAssembly !== 'undefined' && typeof AudioContext !== 'undefined';
}
